/* landfall-mass-entry.js - the Mass entry page (wave 7B, Oct 21 2026).
   Josh: "a masked like card search and have all the cards added to cart,
   kind of how TCG Player does". A shopper pastes a list ("4 Lightning Bolt",
   "1 Sol Ring (CMM)"), we match it against the singles we have in stock and
   add every match to the cart in one go.
   Reads only: the catalog comes from templates/collection.mass-entry.liquid
   (/collections/singles?view=mass-entry&page=N), the cart from /cart.js.
   The one write is the shopper's own cart (/cart/add.js). Nothing here
   touches a product; another tool manages the singles.
   The pure functions at the top are exported for node (tools/test-wave7b.cjs). */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.LandfallMassEntry = api;
    if (typeof document !== 'undefined') {
      var boot = function () { api.mountAll(document); };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
      else boot();
    }
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // Limits live in code, not in the page settings.
  var MAX_LINES = 100;
  var MAX_QTY = 99;
  var CACHE_MS = 15 * 60 * 1000;
  var PAGE_PARALLEL = 2;
  var RETRY_GAP_MS = 120;
  var CONDITIONS = ['Near Mint', 'Lightly Played', 'Moderately Played', 'Heavily Played', 'Damaged'];
  var COND_SHORT = { 'Near Mint': 'NM', 'Lightly Played': 'LP', 'Moderately Played': 'MP', 'Heavily Played': 'HP', 'Damaged': 'DMG' };

  // ------------------------------------------------------------ words --
  // Lower case, no accents, one kind of quote; apostrophes and full stops
  // vanish ("Misty's" = "Mistys", "M.O.D.O.K." = "MODOK"), every other mark
  // is a space.
  function norm(s) {
    return String(s == null ? '' : s)
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[‘’ʼ`']/g, '')
      .replace(/[“”]/g, '"')
      .replace(/\./g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // The collector number as a comparable string: "0043" = "43", "205/165"
  // = "205", "SWSH248" stays "swsh248".
  function numKey(n) {
    var s = String(n == null ? '' : n).trim().toLowerCase().split('/')[0];
    return /^\d+[a-z]?$/.test(s) ? s.replace(/^0+(?=\d)/, '') : s;
  }

  // ------------------------------------------------------- one line --
  var HEADER = /^(deck|main ?deck|mainboard|sideboard|side ?board|commander|commanders|companion|maybeboard|considering|tokens?|about|pok[eé]mon|trainers?|energy|energies|total cards)\s*:?\s*\(?\d*\)?\s*$/i;

  // One pasted line to { qty, name, front, set, num, foil, sb, raw }, or null
  // for a line that is not a card (blank, comment, header).
  function parseLine(raw) {
    var s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
    if (!s || /^(\/\/|#)/.test(s) || HEADER.test(s)) return null;
    var sb = false;
    if (/^sb:\s*/i.test(s)) { sb = true; s = s.replace(/^sb:\s*/i, ''); }
    s = s.replace(/^[-*•]\s+/, '');
    var qty = 1, m;
    if ((m = s.match(/^(\d{1,4})\s*x?\s+(.+)$/i))) { qty = Number(m[1]); s = m[2]; }
    else if ((m = s.match(/^x\s?(\d{1,4})\s+(.+)$/i))) { qty = Number(m[1]); s = m[2]; }
    else if ((m = s.match(/^(.+?)\s+x\s?(\d{1,4})$/i))) { qty = Number(m[2]); s = m[1]; }
    var foil = false;
    if (/\s\*(f|foil|e|etched)\*\s*$/i.test(s) || /\s\*(f|foil|e|etched)\*\s/i.test(s)) {
      foil = true; s = s.replace(/\s\*(f|foil|e|etched)\*/ig, '');
    }
    s = s.replace(/\s\*[a-z]+\*/ig, '').trim();
    var set = '', num = '';
    // (SET) 123, (SET), [SET] 123, Arena style "(M10) 146"
    if ((m = s.match(/^(.+?)\s*[(\[]((?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{2,6})[)\]]\s*(#?\s*[A-Za-z0-9\-]{1,10}(?:\/[A-Za-z0-9]+)?)?\s*$/))) {
      s = m[1]; set = m[2].toUpperCase(); num = m[3] ? m[3].replace(/^#\s*/, '') : '';
    } else if ((m = s.match(/^(.+\S)\s+([A-Z][A-Z0-9]{1,4})\s+(\d{1,4}[a-z]?)$/))) {
      // Pokemon TCG Live: "4 Pikachu ex SVI 57" (an all-caps code, then a number)
      s = m[1]; set = m[2]; num = m[3];
    }
    s = s.trim();
    if (!s) return null;
    var front = s.split(/\s*\/\/\s*/)[0].trim() || s;
    qty = Math.max(1, Math.min(MAX_QTY, qty || 1));
    return { qty: qty, name: s, front: front, set: set, num: num, foil: foil, sb: sb, raw: String(raw).trim() };
  }

  // The whole box: card lines, and how many lines were cut past the cap.
  function parseList(text) {
    var out = [], skipped = 0;
    String(text == null ? '' : text).split(/\r?\n/).forEach(function (l) {
      var p = parseLine(l);
      if (!p) return;
      if (out.length >= MAX_LINES) { skipped++; return; }
      out.push(p);
    });
    return { lines: out, cut: skipped };
  }

  // ------------------------------------------------------ a listing --
  // A singles title to its parts.
  //   "Grand Abolisher (Borderless) [CMM - 625]"
  //   "Mew ex - 205/165 [MEW - 205/165]"
  //   "Kleavor V (Full Art) - SWSH248 [SWSD - SWSH248]"
  //   "Delver of Secrets // Insectile Aberration [ISD - 51]"
  function parseTitle(title) {
    var t = String(title == null ? '' : title).trim();
    var set = '', num = '', m;
    if ((m = t.match(/\s*\[([^\]\-]+?)\s*-\s*([^\]]+)\]\s*$/))) {
      set = m[1].trim().toUpperCase(); num = m[2].trim(); t = t.slice(0, m.index).trim();
    } else if ((m = t.match(/\s*\[([^\]]+)\]\s*$/))) {
      set = m[1].trim().toUpperCase(); t = t.slice(0, m.index).trim();
    }
    var full = t, treat = [], prev;
    do {
      prev = t;
      if ((m = t.match(/\s*\(([^()]*)\)\s*$/))) { treat.unshift(m[1]); t = t.slice(0, m.index).trim(); }
      else if ((m = t.match(/\s+-\s+[A-Za-z]{0,6}\d+[a-z]?(?:\/[A-Za-z0-9]+)?\s*$/))) t = t.slice(0, m.index).trim();
    } while (t !== prev && t);
    if (!t) t = full;
    var front = t.split(/\s+\/\/\s+/)[0].trim();
    return { name: t, full: full, front: front, set: set, num: num, treat: treat };
  }

  function isFoil(item) {
    var f = String(item.f || '').toLowerCase();
    if (f && f !== 'normal') return true;
    return /\bfoil\b/i.test(item.t || '');
  }
  function finishLabel(item) {
    var f = String(item.f || '').toLowerCase();
    if (f === 'reverse-holofoil') return 'Reverse holo';
    if (f === 'holofoil') return 'Holofoil';
    if (f === 'foil' || /\bfoil\b/i.test(item.t || '')) return 'Foil';
    if (f === 'normal') return 'Normal';
    return '';
  }

  // Catalog items to name -> [items]. Each item is indexed under its plain
  // name, its front face and its name with the treatment kept, so "Delver of
  // Secrets" and "B.F.M. (Big Furry Monster)" both find their card.
  function buildIndex(items) {
    var idx = new Map();
    (items || []).forEach(function (it) {
      var p = parseTitle(it.t);
      var rec = Object.assign({}, it, { name: p.name, set: p.set, num: p.num, treat: p.treat });
      var keys = new Set([norm(p.name), norm(p.front), norm(p.full)]);
      keys.forEach(function (k) {
        if (!k) return;
        if (!idx.has(k)) idx.set(k, []);
        idx.get(k).push(rec);
      });
    });
    return idx;
  }

  // ---------------------------------------------------------- match --
  // Exact name only, never a near miss: "Lightning Bolt" is not
  // "Lightning Bolt Token". Ranked: the asked set and number, then the asked
  // set, then foil if *F* was asked, then Near Mint, then the lowest price.
  function rankOf(line, it) {
    var setHit = line.set && it.set === line.set ? 1 : 0;
    var numHit = setHit && line.num && numKey(it.num) === numKey(line.num) ? 1 : 0;
    var foilHit = line.foil ? (isFoil(it) ? 1 : 0) : 0;
    var nm = it.c === 'Near Mint' ? 1 : 0;
    return [-numHit, -setHit, -foilHit, -nm, Number(it.p) || 0, String(it.v)];
  }
  function cmp(a, b) {
    for (var i = 0; i < a.length; i++) { if (a[i] < b[i]) return -1; if (a[i] > b[i]) return 1; }
    return 0;
  }
  function candidates(line, idx) {
    var seen = new Set(), out = [];
    [line.name, line.front].forEach(function (n) {
      (idx.get(norm(n)) || []).forEach(function (it) {
        if (seen.has(String(it.v))) return;
        seen.add(String(it.v)); out.push(it);
      });
    });
    return out.sort(function (a, b) { return cmp(rankOf(line, a), rankOf(line, b)); });
  }
  // Is this pick the printing the shopper asked for?
  function asked(line, it) {
    if (line.set && it.set !== line.set) return false;
    if (line.set && line.num && numKey(it.num) !== numKey(line.num)) return false;
    if (line.foil && !isFoil(it)) return false;
    return true;
  }
  function match(line, idx) {
    var c = candidates(line, idx);
    return { line: line, cands: c, best: c[0] || null };
  }

  // ----------------------------------------------------------- plan --
  // Every matched line against the stock left after what is already in the
  // cart, in list order, so two lines asking for the same card share it.
  // choice[i] = a variant id pins line i to that printing; otherwise a line
  // that runs short is topped up from the next printings in rank order.
  function plan(matches, cartQty, choice) {
    var used = new Map();
    var inCart = cartQty instanceof Map ? cartQty : new Map(Object.entries(cartQty || {}));
    choice = choice || {};
    var left = function (it) {
      if (it.q == null) return Infinity;
      return Math.max(0, Number(it.q) - (Number(inCart.get(String(it.v))) || 0) - (used.get(String(it.v)) || 0));
    };
    return matches.map(function (m, i) {
      var want = m.line.qty, picks = [];
      var pinned = choice[i] != null ? m.cands.filter(function (c) { return String(c.v) === String(choice[i]); })[0] : null;
      var order = pinned ? [pinned] : m.cands;
      var need = want;
      for (var k = 0; k < order.length && need > 0; k++) {
        var it = order[k], take = Math.min(need, left(it));
        if (take <= 0) continue;
        picks.push({ item: it, qty: take });
        used.set(String(it.v), (used.get(String(it.v)) || 0) + take);
        need -= take;
      }
      var got = want - need;
      var heldInCart = m.cands.reduce(function (n, c) { return n + (Number(inCart.get(String(c.v))) || 0); }, 0);
      var state;
      if (!m.cands.length) state = 'none';
      else if (!got) state = 'short';
      else if (need > 0) state = 'short';
      else if (picks.some(function (p) { return !asked(m.line, p.item); })) state = 'sub';
      else if (new Set(picks.map(function (p) { return p.item.set + ' ' + numKey(p.item.num); })).size > 1) state = 'sub';
      else state = 'found';
      var cost = picks.reduce(function (n, p) { return n + p.qty * (Number(p.item.p) || 0); }, 0);
      return { i: i, line: m.line, cands: m.cands, picks: picks, want: want, got: got, short: need, inCart: heldInCart,
        state: state, cost: cost, pinned: pinned ? String(pinned.v) : null };
    });
  }

  // What goes to /cart/add.js: one entry per variant, quantities summed.
  function cartItems(rows, ticked) {
    var by = new Map();
    rows.forEach(function (r) {
      if (ticked && ticked[r.i] === false) return;
      r.picks.forEach(function (p) { by.set(String(p.item.v), (by.get(String(p.item.v)) || 0) + p.qty); });
    });
    return Array.from(by.entries()).map(function (e) { return { id: Number(e[0]), quantity: e[1] }; });
  }

  // ---------------------------------------------------------- cart --
  function cartAddUrl() {
    var r = (typeof window !== 'undefined' && window.routes) || {};
    return (r.cart_add_url || '/cart/add').replace(/\.js$/, '') + '.js';
  }
  function jsonConfig(body) {
    // The theme's fetchConfig() when it is there, so headers match its own adds.
    var cfg = (typeof fetchConfig === 'function') ? fetchConfig('json') : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    cfg.headers = Object.assign({}, cfg.headers, { 'X-Requested-With': 'XMLHttpRequest' });
    cfg.body = JSON.stringify(body);
    return cfg;
  }
  var wait = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };

  async function readCart(fetchFn) {
    var res = await fetchFn('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart ' + res.status);
    var c = await res.json();
    var m = new Map();
    (c.items || []).forEach(function (it) { m.set(String(it.variant_id || it.id), (m.get(String(it.variant_id || it.id)) || 0) + Number(it.quantity || 0)); });
    return m;
  }

  // One request for the whole list. If Shopify refuses it (422: a card sold
  // a moment ago, a cap), read the cart again and add what is still missing
  // one card at a time, keeping Shopify's own words for each that fails.
  // Returns { ok, added, failed: [{ id, quantity, message }], sections }.
  async function addAll(items, opt) {
    opt = opt || {};
    var fetchFn = opt.fetch || (typeof fetch !== 'undefined' ? fetch.bind(self) : null);
    var sections = opt.sections || 'cart-drawer,cart-icon-bubble';
    var sectionsUrl = opt.sectionsUrl || (typeof location !== 'undefined' ? location.pathname : '/');
    var gap = opt.gap == null ? RETRY_GAP_MS : opt.gap;
    var url = opt.url || cartAddUrl();
    if (!items.length) return { ok: true, added: 0, failed: [], sections: null };
    var before = opt.before || await readCart(fetchFn);
    var res = await fetchFn(url, jsonConfig({ items: items, sections: sections, sections_url: sectionsUrl }));
    var body = null;
    try { body = await res.json(); } catch (e) { body = null; }
    if (res.ok) {
      return { ok: true, added: items.reduce(function (n, it) { return n + it.quantity; }, 0), failed: [], sections: body && body.sections || null };
    }
    var now = await readCart(fetchFn);
    var failed = [], added = 0, lastSections = null;
    for (var i = 0; i < items.length; i++) {
      var it = items[i], id = String(it.id);
      var already = Math.max(0, (now.get(id) || 0) - (before.get(id) || 0));
      var rest = it.quantity - already;
      added += Math.min(already, it.quantity);
      if (rest <= 0) continue;
      if (i) await wait(gap);
      var r = await fetchFn(url, jsonConfig({ items: [{ id: it.id, quantity: rest }], sections: sections, sections_url: sectionsUrl }));
      var b = null;
      try { b = await r.json(); } catch (e) { b = null; }
      if (r.ok) { added += rest; if (b && b.sections) lastSections = b.sections; }
      else failed.push({ id: it.id, quantity: rest, message: (b && (b.description || b.message)) || ('Shopify answered ' + r.status) });
    }
    return { ok: !failed.length, added: added, failed: failed, sections: lastSections, firstError: body && (body.description || body.message) || null };
  }

  // ------------------------------------------------------- catalog --
  async function loadCatalog(handle, fetchFn, store) {
    var key = 'lfm-cat-v2-' + handle;
    try {
      var hit = store && JSON.parse(store.getItem(key) || 'null');
      if (hit && Date.now() - hit.at < CACHE_MS && Array.isArray(hit.items)) return hit.items;
    } catch (e) { /* storage blocked or full: read fresh */ }
    var base = '/collections/' + encodeURIComponent(handle) + '?view=mass-entry&page=';
    // No Accept: application/json here: with it Shopify answers with its own
    // collection JSON instead of rendering the mass-entry view.
    var get = async function (n) {
      var r = await fetchFn(base + n, { credentials: 'same-origin' });
      if (!r.ok) throw new Error('catalog page ' + n + ': ' + r.status);
      var j = await r.json();
      if (!j || !Array.isArray(j.items)) throw new Error('catalog page ' + n + ' is not the mass-entry view');
      return j;
    };
    var first = await get(1);
    var items = (first.items || []).slice();
    var pages = Math.min(Number(first.pages) || 1, 100);
    for (var p = 2; p <= pages; p += PAGE_PARALLEL) {
      var batch = [];
      for (var q = p; q < p + PAGE_PARALLEL && q <= pages; q++) batch.push(get(q));
      (await Promise.all(batch)).forEach(function (j) { items = items.concat(j.items || []); });
    }
    try { if (store && items.length) store.setItem(key, JSON.stringify({ at: Date.now(), items: items })); } catch (e) { /* too big or blocked */ }
    return items;
  }

  // ------------------------------------------------------------ page --
  var esc = function (s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  };
  var USD = (typeof Intl !== 'undefined') ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }) : null;
  function money(cents) {
    var v = (Number(cents) || 0) / 100;
    return USD ? USD.format(v) : '$' + v.toFixed(2);
  }
  var STATE = { added: 'In your cart', found: 'Found', sub: 'Different printing', short: 'Short', none: 'None in stock' };

  function optionLabel(it) {
    return [it.set + (it.num ? ' ' + it.num : ''), COND_SHORT[it.c] || it.c || '', finishLabel(it), money(it.p)].filter(Boolean).join(' · ');
  }

  function mount(el) {
    if (el.__lfm) return;
    el.__lfm = true;
    var $ = function (s) { return el.querySelector(s); };
    var handle = el.getAttribute('data-catalog') || 'singles';
    var form = $('[data-lfm-form]'), box = $('[data-lfm-list]'), status = $('[data-lfm-status]');
    var results = $('[data-lfm-results]'), list = $('[data-lfm-rows]'), sum = $('[data-lfm-sum]');
    var addBtn = $('[data-lfm-add]'), findBtn = $('[data-lfm-find]'), missWrap = $('[data-lfm-miss]'), missBox = $('[data-lfm-missbox]');
    var store = null;
    try { store = window.sessionStorage; } catch (e) { store = null; }
    var idx = null, matches = [], rows = [], choice = {}, ticked = {}, inCart = new Map(), busy = false, done = null;

    try { var draft = store && store.getItem('lfm-draft'); if (draft && !box.value) box.value = draft; } catch (e) { /* no storage */ }
    box.addEventListener('input', function () { try { store && store.setItem('lfm-draft', box.value); } catch (e) { /* no storage */ } });

    var say = function (msg, bad) { status.textContent = msg; status.classList.toggle('lfm__status--bad', !!bad); };

    async function ensureIndex() {
      if (idx) return idx;
      say('Reading what we have in stock…');
      var items = await loadCatalog(handle, fetch.bind(window), store);
      idx = buildIndex(items);
      return idx;
    }

    function paint() {
      rows = plan(matches, inCart, choice);
      var lines = rows.length, cards = 0, cost = 0, found = 0;
      rows.forEach(function (r) { if (ticked[r.i] !== false) { cards += r.got; cost += r.cost; } if (r.got) found++; });
      list.innerHTML = rows.map(rowHtml).join('');
      sum.innerHTML = '<span><b>' + found + '</b> of ' + lines + ' line' + (lines === 1 ? '' : 's') + ' found</span>' +
        '<span><b>' + cards + '</b> card' + (cards === 1 ? '' : 's') + ' ticked</span>' +
        '<span class="lfm__sumtotal">Total <b>' + money(cost) + '</b></span>';
      addBtn.disabled = !cards || busy || !!done;
      addBtn.textContent = done ? 'Added to cart' : cards ? 'Add all to cart (' + cards + ')' : 'Add all to cart';
      var miss = rows.filter(function (r) { return r.short > 0; })
        .map(function (r) { return r.short + ' ' + r.line.name + (r.line.set ? ' (' + r.line.set + ')' + (r.line.num ? ' ' + r.line.num : '') : '') + (r.line.foil ? ' *F*' : ''); });
      missWrap.hidden = !miss.length;
      missBox.value = miss.join('\n');
      results.hidden = false;
    }

    function rowHtml(r) {
      var st = r.state, label = STATE[st];
      if (st === 'short') label = r.got ? 'Short by ' + r.short : (r.inCart ? 'All ours are in your cart' : 'None left');
      var head = r.picks[0] && r.picks[0].item;
      var on = ticked[r.i] !== false && r.got > 0;
      var why = '';
      if (done && on && r.picks.length) {
        why = r.picks.map(function (p) { return done.failed[String(p.item.v)]; }).filter(Boolean)[0] || '';
        if (why) { st = 'short'; label = 'Not added'; }
        else { st = 'added'; label = 'In your cart'; }
      }
      var tick = '<label class="lfm__tick"><input type="checkbox" data-lfm-tick="' + r.i + '"' + (on ? ' checked' : '') + (r.got ? '' : ' disabled') +
        ' aria-label="Add ' + esc(r.line.name) + '"><span aria-hidden="true"></span></label>';
      var img = head && head.img ? '<img class="lfm__img" src="' + esc(head.img) + '" alt="" width="60" height="84" loading="lazy">' : '<span class="lfm__img lfm__img--none" aria-hidden="true"></span>';
      var picks = r.picks.map(function (p) {
        var it = p.item;
        var bits = [it.vt, it.c, finishLabel(it)].filter(Boolean).map(esc).join(' · ');
        return '<li><span class="lfm__pq">' + p.qty + '×</span> <span class="lfm__pt">' + esc(it.t) + '</span>' +
          '<span class="lfm__pm">' + bits + '</span><span class="lfm__pp">' + money(it.p) + ' each</span></li>';
      }).join('');
      var sel = '';
      if (r.cands.length > 1) {
        var opts = '<option value="">Best match' + (r.cands.length ? ' (we pick)' : '') + '</option>' + r.cands.map(function (c) {
          return '<option value="' + esc(c.v) + '"' + (r.pinned === String(c.v) ? ' selected' : '') + '>' + esc(optionLabel(c)) +
            (c.q != null ? ' · ' + c.q + ' in stock' : '') + '</option>';
        }).join('');
        sel = '<label class="lfm__pick"><span>Other printings (' + (r.cands.length - 1) + ')</span><select data-lfm-pick="' + r.i + '">' + opts + '</select></label>';
      }
      var note = '';
      if (why) note = '<p class="lfm__note">' + esc(why) + '</p>';
      else if (st === 'none') note = '<p class="lfm__note">No in-stock single with this exact name. Check the spelling, or ask us to order it.</p>';
      else if (r.short > 0 && r.got) note = '<p class="lfm__note">Only ' + r.got + ' left' + (r.inCart ? ' after the ' + r.inCart + ' already in your cart' : '') + '.</p>';
      else if (r.short > 0 && r.inCart) note = '<p class="lfm__note">Your cart already holds the ' + r.inCart + ' we have.</p>';
      else if (st === 'sub' && !r.line.set && !r.line.foil) note = '<p class="lfm__note">We do not have ' + r.want + ' of one printing, so this line is split across printings.</p>';
      else if (st === 'sub') note = '<p class="lfm__note">We do not have the printing you asked for' + (r.line.set ? ' (' + esc(r.line.set) + (r.line.foil ? ', foil' : '') + ')' : (r.line.foil ? ' (foil)' : '')) + ', so this is the closest we have.</p>';
      return '<li class="lfm__row lfm__row--' + st + (on ? '' : ' lfm__row--off') + '">' + tick + img +
        '<div class="lfm__main"><div class="lfm__top"><span class="lfm__want">' + r.want + '× ' + esc(r.line.name) +
        (r.line.set ? ' <span class="lfm__ask">(' + esc(r.line.set) + (r.line.num ? ' ' + esc(r.line.num) : '') + ')</span>' : '') +
        (r.line.foil ? ' <span class="lfm__ask">foil</span>' : '') + '</span>' +
        '<span class="lfm__state lfm__state--' + st + '">' + esc(label) + '</span></div>' +
        (picks ? '<ul class="lfm__picks">' + picks + '</ul>' : '') + note + sel + '</div>' +
        '<div class="lfm__cost">' + (r.got ? money(r.cost) : '') + '</div></li>';
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      if (busy) return;
      var parsed = parseList(box.value);
      if (!parsed.lines.length) { say('Paste at least one card, like 4 Lightning Bolt.', true); box.focus(); return; }
      busy = true; findBtn.disabled = true;
      try {
        await ensureIndex();
        try { inCart = await readCart(fetch.bind(window)); } catch (er) { inCart = new Map(); }
        matches = parsed.lines.map(function (l) { return match(l, idx); });
        choice = {}; ticked = {}; done = null;
        paint();
        var miss = rows.filter(function (r) { return r.short > 0; }).length;
        say('Checked ' + rows.length + ' line' + (rows.length === 1 ? '' : 's') + '. ' +
          (miss ? miss + ' need' + (miss === 1 ? 's' : '') + ' a look.' : 'Everything is in stock.') +
          (parsed.cut ? ' Only the first ' + MAX_LINES + ' lines were read.' : ''));
        results.querySelector('h2').focus();
      } catch (er) {
        say('We could not read the shop just now. Try again in a minute, or call (423) 237-6248.', true);
      } finally { busy = false; findBtn.disabled = false; if (rows.length) addBtn.disabled = !rows.some(function (r) { return r.got && ticked[r.i] !== false; }); }
    });

    list.addEventListener('change', function (e) {
      var t = e.target;
      if (done) return;
      if (t.matches('[data-lfm-pick]')) {
        var i = Number(t.getAttribute('data-lfm-pick'));
        if (t.value) choice[i] = t.value; else delete choice[i];
        paint();
        var again = list.querySelector('[data-lfm-pick="' + i + '"]'); if (again) again.focus();
      } else if (t.matches('[data-lfm-tick]')) {
        var j = Number(t.getAttribute('data-lfm-tick'));
        ticked[j] = t.checked;
        paint();
        var back = list.querySelector('[data-lfm-tick="' + j + '"]'); if (back) back.focus();
      }
    });

    addBtn.addEventListener('click', async function () {
      if (busy) return;
      var items = cartItems(rows, ticked);
      if (!items.length) return;
      busy = true; addBtn.disabled = true; addBtn.textContent = 'Adding…';
      try {
        var res = await addAll(items, { before: inCart });
        var cart = document.querySelector('cart-drawer');
        var secs = res.sections;
        if (!secs && res.added) {
          try { var g = await fetch(location.pathname + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' }); if (g.ok) secs = await g.json(); } catch (er) { secs = null; }
        }
        if (res.failed.length) {
          say('Added ' + res.added + ' card' + (res.added === 1 ? '' : 's') + '. ' + res.failed.length + ' could not be added: ' +
            res.failed.map(function (f) { return f.message; }).join(' '), true);
        } else {
          say('Added ' + res.added + ' card' + (res.added === 1 ? '' : 's') + ' to your cart.');
        }
        done = { failed: {} };
        res.failed.forEach(function (f) { done.failed[String(f.id)] = f.message; });
        if (res.added) {
          if (cart && typeof cart.renderContents === 'function' && secs) {
            cart.classList.remove('is-empty');
            cart.renderContents({ sections: secs });
          } else if (!res.failed.length) {
            window.location.href = ((window.routes && window.routes.cart_url) || '/cart');
            return;
          }
        }
      } catch (er) {
        say('The cart did not answer. Nothing may have been added; open the cart to check.', true);
      } finally {
        busy = false;
        if (matches.length) paint();
      }
    });

    var copyBtn = $('[data-lfm-copy]');
    if (copyBtn) copyBtn.addEventListener('click', async function () {
      try { await navigator.clipboard.writeText(missBox.value); copyBtn.textContent = 'Copied'; }
      catch (e) { missBox.select(); copyBtn.textContent = 'Press Ctrl+C to copy'; }
      setTimeout(function () { copyBtn.textContent = 'Copy this list'; }, 2500);
    });
    var clearBtn = $('[data-lfm-clear]');
    if (clearBtn) clearBtn.addEventListener('click', function () {
      box.value = ''; try { store && store.removeItem('lfm-draft'); } catch (e) { /* no storage */ }
      matches = []; rows = []; results.hidden = true; say(''); box.focus();
    });
  }

  function mountAll(doc) {
    Array.prototype.forEach.call(doc.querySelectorAll('[data-lfm]'), mount);
  }

  return {
    MAX_LINES: MAX_LINES, MAX_QTY: MAX_QTY, CONDITIONS: CONDITIONS,
    norm: norm, numKey: numKey, parseLine: parseLine, parseList: parseList, parseTitle: parseTitle,
    buildIndex: buildIndex, match: match, plan: plan, cartItems: cartItems, addAll: addAll,
    loadCatalog: loadCatalog, readCart: readCart, isFoil: isFoil, finishLabel: finishLabel, mountAll: mountAll
  };
});
